"use client";

import { useState, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Mail, Lock, LogIn, AlertCircle, Eye, EyeOff } from "lucide-react";

export default function LoginForm() {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [mode, setMode]         = useState<"login" | "magic">("login");
  const [magicSent, setMagicSent] = useState(false);
  const router      = useRouter();
  const searchParams = useSearchParams();
  const redirectTo  = searchParams.get("redirectTo") || "/";

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const { signInWithEmailAndPassword } = await import("firebase/auth");
      const { auth } = await import("@/lib/firebase/client");
      
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const idToken = await userCredential.user.getIdToken();

      const res = await fetch("/api/auth/session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken })
      });

      const resData = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(resData.error || "No se pudo iniciar la sesión en el servidor");
      }

      const role = resData.role || "USER";
      const isGestor = role === "GESTOR" || role === "ADMIN";
      
      if (isGestor) {
        router.push("/gestor");
      } else {
        const safeRedirect = redirectTo.startsWith("/gestor") ? "/" : redirectTo;
        router.push(safeRedirect);
      }
      router.refresh();
    } catch (err: any) {
      console.error("Login error:", err);
      setError(err.message || "Credenciales inválidas");
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const { sendSignInLinkToEmail } = await import("firebase/auth");
      const { auth } = await import("@/lib/firebase/client");

      await sendSignInLinkToEmail(auth, email, {
        url: `${window.location.origin}/auth/callback?next=${redirectTo}`,
        handleCodeInApp: true
      });

      window.localStorage.setItem("emailForSignIn", email);
      setMagicSent(true);
    } catch (err: any) {
      setError(err.message || "Error al enviar el enlace de acceso");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      width: "100%", maxWidth: "420px", padding: "0 16px",
      position: "relative", zIndex: 1,
    }}>
      {/* Card */}
      <div style={{
        background: "var(--md-sys-color-surface)",
        border: "1px solid var(--md-sys-color-outline-variant)",
        borderRadius: "24px",
        padding: "40px 36px",
        boxShadow: "var(--md-sys-elevation-3)",
        color: "var(--md-sys-color-on-surface)"
      }}>
        {/* Logo / Brand */}
        <div style={{ textAlign: "center", marginBottom: "32px", display: "flex", flexDirection: "column", alignItems: "center", gap: "16px" }}>
          <div style={{
            background: "var(--md-sys-color-primary)",
            borderRadius: "16px",
            padding: "16px",
            display: "inline-flex",
            justifyContent: "center",
            alignItems: "center",
            boxShadow: "0 8px 32px rgba(0,0,0,0.1)"
          }}>
            <img src="/logos/imagotipo-blanco.svg" alt="Qentax Icon" style={{ height: "48px", width: "48px", display: "block" }} />
          </div>
          <div>
            <img src="/logos/logo-azul.svg" alt="Qentax Logo" className="light-mode-logo" style={{ height: "32px", display: "block", margin: "0 auto" }} />
            <img src="/logos/logo-blanco.svg" alt="Qentax Logo" className="dark-mode-logo" style={{ height: "32px", display: "block", margin: "0 auto" }} />
            <p style={{ color: "var(--md-sys-color-on-surface-variant)", margin: "8px 0 0", fontSize: "0.875rem", fontWeight: 500 }}>Gestión para autónomos</p>
          </div>
        </div>

        {/* Mode toggle */}
        <div style={{ display: "flex", background: "var(--md-sys-color-surface-variant)", borderRadius: "10px", padding: "4px", marginBottom: "24px", gap: "4px" }}>
          {(["login", "magic"] as const).map(m => (
            <button key={m} onClick={() => { setMode(m); setError(""); setMagicSent(false); }} style={{
              flex: 1, padding: "8px", borderRadius: "8px", border: "none", cursor: "pointer", fontWeight: 700, fontSize: "0.8125rem", transition: "all 0.2s",
              background: mode === m ? "var(--md-sys-color-primary)" : "transparent",
              color: mode === m ? "var(--md-sys-color-on-primary)" : "var(--md-sys-color-on-surface-variant)",
            }}>
              {m === "login" ? "Contraseña" : "Magic Link"}
            </button>
          ))}
        </div>

        {magicSent ? (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ fontSize: "3rem", marginBottom: "12px" }}>📬</div>
            <p style={{ color: "var(--md-sys-color-on-surface)", fontWeight: 700, margin: "0 0 8px" }}>Revisa tu email</p>
            <p style={{ color: "var(--md-sys-color-on-surface-variant)", fontSize: "0.875rem", margin: 0 }}>
              Hemos enviado un enlace de acceso a <strong>{email}</strong>
            </p>
            <button onClick={() => setMagicSent(false)} style={{ marginTop: "20px", background: "none", border: "1px solid var(--md-sys-color-outline)", color: "var(--md-sys-color-on-surface-variant)", padding: "8px 16px", borderRadius: "8px", cursor: "pointer", fontSize: "0.875rem" }}>
              Volver
            </button>
          </div>
        ) : (
          <form onSubmit={mode === "login" ? handleLogin : handleMagicLink} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            {/* Email */}
            <div>
              <label style={{ display: "block", color: "var(--md-sys-color-on-surface-variant)", fontSize: "0.78rem", fontWeight: 700, marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.06em" }}>Email</label>
              <div style={{ position: "relative" }}>
                <Mail size={16} style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "var(--md-sys-color-on-surface-variant)", pointerEvents: "none", opacity: 0.5 }} />
                <input
                  type="email" required value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  style={{ width: "100%", padding: "12px 14px 12px 40px", background: "var(--md-sys-color-surface-variant)", border: "1px solid var(--md-sys-color-outline)", borderRadius: "10px", color: "var(--md-sys-color-on-surface)", fontSize: "0.9375rem", outline: "none", boxSizing: "border-box" }}
                />
              </div>
            </div>

            {/* Password */}
            {mode === "login" && (
              <div>
                <label style={{ display: "block", color: "var(--md-sys-color-on-surface-variant)", fontSize: "0.78rem", fontWeight: 700, marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.06em" }}>Contraseña</label>
                <div style={{ position: "relative" }}>
                  <Lock size={16} style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "var(--md-sys-color-on-surface-variant)", pointerEvents: "none", opacity: 0.5 }} />
                  <input
                    type={showPassword ? "text" : "password"} required value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    style={{ width: "100%", padding: "12px 72px 12px 40px", background: "var(--md-sys-color-surface-variant)", border: "1px solid var(--md-sys-color-outline)", borderRadius: "10px", color: "var(--md-sys-color-on-surface)", fontSize: "0.9375rem", outline: "none", boxSizing: "border-box" }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: "absolute", right: "36px", top: "50%", transform: "translateY(-50%)",
                      background: "none", border: "none", color: "var(--md-sys-color-on-surface-variant)", cursor: "pointer", padding: "4px", opacity: 0.6
                    }}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div style={{ display: "flex", alignItems: "center", gap: "8px", background: "var(--md-sys-color-error-container)", border: "1px solid var(--md-sys-color-error)", borderRadius: "8px", padding: "10px 14px" }}>
                <AlertCircle size={16} style={{ color: "var(--md-sys-color-error)", flexShrink: 0 }} />
                <p style={{ color: "var(--md-sys-color-on-error-container)", fontSize: "0.8125rem", margin: 0 }}>{error}</p>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit" disabled={loading}
              style={{
                width: "100%", padding: "14px", borderRadius: "12px", border: "none", cursor: loading ? "not-allowed" : "pointer",
                background: "var(--md-sys-color-primary)", color: "var(--md-sys-color-on-primary)", fontWeight: 800, fontSize: "1rem",
                opacity: loading ? 0.7 : 1, marginTop: "4px", boxShadow: "0 4px 16px rgba(63,81,181,0.2)", transition: "all 0.2s",
              }}
            >
              {loading ? "Entrando…" : mode === "login" ? "Iniciar sesión" : "Enviar Magic Link"}
            </button>
          </form>
        )}
      </div>

      <p style={{ textAlign: "center", color: "var(--md-sys-color-on-surface-variant)", opacity: 0.5, fontSize: "0.75rem", marginTop: "20px" }}>
        Qentax · Datos cifrados por Firebase
      </p>
    </div>
  );
}
